import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import FontAwesomeIcon from 'react-fontawesome';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { FormApi, ValidationErrors } from 'final-form';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { Store, User } from './types';
import { saveUpdateUser } from './actions/Actions';
import { EMPLOYEES_NS, required } from './Constants';

type FormValues = Partial<User>;

const DEFAULT_PASSWORD = 'Ch@ngeMe123';

const Employees: React.FC<RouteComponentProps> = () => {
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const [selectedUser, setSelectedUser] = React.useState<FormValues>(null);
    const [loading, setLoading] = React.useState(false);
    const { t } = useTranslation();

    const formValues: FormValues = { ...selectedUser };
    const users = useSelector(({ app }: Store) => app.users);

    if (!selectedUser) {
        formValues.password = DEFAULT_PASSWORD;
    }

    const validateForm = React.useCallback(
        (values: FormValues) => {
            const errors: ValidationErrors = {};

            if (!/\S+@\S+\.\S+/.test(values.email)) {
                errors.email = t(`${EMPLOYEES_NS}.invalid_email`);
            }

            return errors;
        },
        [t]
    );

    const updateSelectedUser = React.useCallback(
        (user: User) => () => {
            setSelectedUser(user);
        },
        []
    );

    const resetStateAndForm = React.useCallback(() => {
        setSelectedUser(null);
    }, []);

    const handleFormSubmit = React.useCallback(
        (values: User, form: FormApi) => {
            setLoading(true);

            dispatch(saveUpdateUser(values))
                .then(() => {
                    resetStateAndForm();
                    form.reset({ password: DEFAULT_PASSWORD });

                    ['userName', 'fullName', 'email'].forEach(field => {
                        form.resetFieldState(field);
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, resetStateAndForm]
    );

    const renderForm = React.useCallback(
        ({ handleSubmit, submitting, validating }: FormRenderProps) => {
            const disabled = submitting || validating;

            return (
                <form className='form' onSubmit={handleSubmit}>
                    <label className='form-field'>
                        <label className='small-wide'>{t('username')}:</label>
                        <Field name='userName' validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input
                                        {...input}
                                        type='text'
                                        disabled={!!selectedUser}
                                    />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${EMPLOYEES_NS}.name`)}:
                        </label>
                        <Field name='fullName' validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='text' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>E-MAIL:</label>
                        <Field name='email' validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input
                                        {...input}
                                        type='email'
                                        disabled={!!selectedUser}
                                    />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${EMPLOYEES_NS}.phone`)}:
                        </label>
                        <Field name='phoneNumber'>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='tel' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    {!selectedUser && (
                        <label className='form-field'>
                            <label className='small-wide'>{t('password')}:</label>
                            <Field name='password'>
                                {({ input }) => (
                                    <div className='field-input'>
                                        <input {...input} disabled />
                                    </div>
                                )}
                            </Field>
                        </label>
                    )}
                    <div className='action-buttons'>
                        <div>
                            <button className='save' type='submit' disabled={disabled}>
                                {t('save')}
                            </button>
                            {loading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                        {/* <button className='delete' type='button' disabled={disabled}>
                        ΔΙΑΓΡΑΦΗ
                    </button> */}
                    </div>
                </form>
            );
        },
        [loading, selectedUser, t]
    );

    return (
        <div className='admin-form main-content products'>
            <Sidebar>
                <FontAwesomeIcon
                    name='plus'
                    className='add-new'
                    onClick={resetStateAndForm}
                />
                <div className='content'>
                    {users.map(user => (
                        <div
                            className={classnames('item', {
                                selected:
                                    user.userName === _.get(selectedUser, 'userName')
                            })}
                            key={user.userName}
                            onClick={updateSelectedUser(user)}>
                            {user.userName}
                        </div>
                    ))}
                </div>
            </Sidebar>
            <div className='products-form'>
                <Form
                    initialValues={{ ...formValues }}
                    render={renderForm}
                    onSubmit={handleFormSubmit}
                    validate={validateForm}
                />
            </div>
        </div>
    );
};

export default Employees;
